import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { 
  Database, 
  Download, 
  RefreshCw, 
  Table, 
  AlertCircle,
  CheckCircle,
  Clock,
  HardDrive,
  Activity,
  Package
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import * as XLSX from 'xlsx';
import JSZip from 'jszip';

interface TablaInfo {
  nombre: string;
  registros: number;
  tamano: string;
}

interface EstadisticasDB {
  totalTablas: number;
  totalRegistros: number;
  tamanoTotal: string;
  ultimaActualizacion: string;
}

export const GestionBaseDatos = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [progresoExportacion, setProgresoExportacion] = useState(0);
  const [tablaActual, setTablaActual] = useState("");
  const [tablas, setTablas] = useState<TablaInfo[]>([]);
  const [estadisticas, setEstadisticas] = useState<EstadisticasDB | null>(null);

  // Lista completa de todas las tablas a exportar
  const TODAS_LAS_TABLAS = [
    'obligaciones_ambientales',
    'evidencias_archivos',
    'documentos_legales',
    'documentos_sociales',
    'cumplimientos_documentos',
    'evidencias_cumplimiento',
    'planes_manejo',
    'profiles',
    'user_roles',
    'actos_administrativos_comentarios'
  ];

  useEffect(() => {
    cargarInformacionDB();
  }, []);

  const cargarInformacionDB = async () => {
    setLoading(true);
    try {
      // Obtener información de las tablas principales
      const tablasInfo: TablaInfo[] = [];
      
      // Obligaciones Ambientales
      const { count: countObligaciones } = await supabase
        .from('obligaciones_ambientales')
        .select('*', { count: 'exact', head: true });
      
      tablasInfo.push({
        nombre: 'obligaciones_ambientales',
        registros: countObligaciones || 0,
        tamano: 'N/A'
      });

      // Evidencias
      const { count: countEvidencias } = await supabase
        .from('evidencias_archivos')
        .select('*', { count: 'exact', head: true });
      
      tablasInfo.push({
        nombre: 'evidencias_archivos',
        registros: countEvidencias || 0,
        tamano: 'N/A'
      });

      // Documentos Legales
      const { count: countLegales } = await supabase
        .from('documentos_legales')
        .select('*', { count: 'exact', head: true });
      
      tablasInfo.push({
        nombre: 'documentos_legales',
        registros: countLegales || 0,
        tamano: 'N/A'
      });

      // Documentos Sociales
      const { count: countSociales } = await supabase
        .from('documentos_sociales')
        .select('*', { count: 'exact', head: true });
      
      tablasInfo.push({
        nombre: 'documentos_sociales',
        registros: countSociales || 0,
        tamano: 'N/A'
      });

      // Perfiles
      const { count: countProfiles } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });
      
      tablasInfo.push({
        nombre: 'profiles',
        registros: countProfiles || 0,
        tamano: 'N/A'
      });

      // Roles
      const { count: countRoles } = await supabase
        .from('user_roles')
        .select('*', { count: 'exact', head: true });
      
      tablasInfo.push({
        nombre: 'user_roles',
        registros: countRoles || 0,
        tamano: 'N/A'
      });

      setTablas(tablasInfo);

      // Calcular estadísticas
      const totalRegistros = tablasInfo.reduce((sum, t) => sum + t.registros, 0);
      setEstadisticas({
        totalTablas: tablasInfo.length,
        totalRegistros,
        tamanoTotal: 'N/A',
        ultimaActualizacion: new Date().toLocaleString('es-ES')
      });

      toast({
        title: "Información actualizada",
        description: "Estadísticas de la base de datos cargadas correctamente",
      });
    } catch (error) {
      console.error('Error al cargar información de la DB:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar la información de la base de datos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const exportarDatos = async (tabla: string) => {
    try {
      const { data, error } = await supabase
        .from(tabla as any)
        .select('*');

      if (error) throw error;

      // Crear workbook de Excel
      const wb = XLSX.utils.book_new();
      
      // Hoja de datos
      const ws = XLSX.utils.json_to_sheet(data || []);
      XLSX.utils.book_append_sheet(wb, ws, "Datos");
      
      // Hoja de metadata
      const metadata = [
        { Campo: 'Tabla', Valor: tabla },
        { Campo: 'Total de registros', Valor: data?.length || 0 },
        { Campo: 'Fecha de exportación', Valor: new Date().toLocaleString('es-ES') },
        { Campo: 'Columnas', Valor: data && data.length > 0 ? Object.keys(data[0]).join(', ') : 'N/A' }
      ];
      const wsMetadata = XLSX.utils.json_to_sheet(metadata);
      XLSX.utils.book_append_sheet(wb, wsMetadata, "Metadata");
      
      // Generar archivo Excel
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${tabla}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Exportación exitosa",
        description: `Datos de ${tabla} exportados en formato Excel`,
      });
    } catch (error) {
      console.error('Error al exportar:', error);
      toast({
        title: "Error",
        description: "No se pudieron exportar los datos",
        variant: "destructive",
      });
    }
  };

  const exportarTodasLasTablas = async () => {
    setExportando(true);
    setProgresoExportacion(0);
    
    const zip = new JSZip();
    const errores: string[] = [];
    let tablasExportadas = 0;

    try {
      toast({
        title: "Iniciando exportación",
        description: "Exportando base de datos completa...",
      });

      // Procesar cada tabla
      for (let i = 0; i < TODAS_LAS_TABLAS.length; i++) {
        const tabla = TODAS_LAS_TABLAS[i];
        setTablaActual(tabla);
        setProgresoExportacion(((i + 1) / TODAS_LAS_TABLAS.length) * 100);

        try {
          const { data, error } = await supabase
            .from(tabla as any)
            .select('*');

          if (error) throw error;

          // Crear workbook de Excel para esta tabla
          const wb = XLSX.utils.book_new();
          
          // Hoja de datos
          const ws = XLSX.utils.json_to_sheet(data || []);
          XLSX.utils.book_append_sheet(wb, ws, "Datos");
          
          // Hoja de metadata
          const metadata = [
            { Campo: 'Tabla', Valor: tabla },
            { Campo: 'Total de registros', Valor: data?.length || 0 },
            { Campo: 'Fecha de exportación', Valor: new Date().toLocaleString('es-ES') },
            { Campo: 'Columnas', Valor: data && data.length > 0 ? Object.keys(data[0]).length : 0 },
            { Campo: 'Lista de columnas', Valor: data && data.length > 0 ? Object.keys(data[0]).join(', ') : 'Tabla vacía' }
          ];
          const wsMetadata = XLSX.utils.json_to_sheet(metadata);
          XLSX.utils.book_append_sheet(wb, wsMetadata, "Metadata");
          
          // Convertir a buffer y agregar al ZIP
          const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          zip.file(`${tabla}.xlsx`, excelBuffer);
          
          tablasExportadas++;
        } catch (error) {
          console.error(`Error al exportar tabla ${tabla}:`, error);
          errores.push(`${tabla}: ${error instanceof Error ? error.message : 'Error desconocido'}`);
        }
      }

      // Crear README.txt
      const totalRegistros = tablas.reduce((sum, t) => sum + t.registros, 0);
      const readme = `VIABILIZA - Exportación Completa de Base de Datos
================================================

Información General:
-------------------
Fecha de exportación: ${new Date().toLocaleString('es-ES')}
Total de tablas: ${TODAS_LAS_TABLAS.length}
Tablas exportadas exitosamente: ${tablasExportadas}
Total de registros: ${totalRegistros.toLocaleString('es-ES')}
Formato: Excel (.xlsx)

Tablas Incluidas:
----------------
${TODAS_LAS_TABLAS.map((t, i) => `${i + 1}. ${t}`).join('\n')}

Estructura de Archivos Excel:
-----------------------------
Cada archivo Excel contiene dos hojas:
1. "Datos" - Todos los registros de la tabla
2. "Metadata" - Información sobre la estructura y exportación

Notas Importantes:
-----------------
- Los UUIDs se mantienen como texto para preservar el formato exacto
- Las fechas están en formato ISO 8601
- Los campos JSON/JSONB están formateados como texto
- Los valores NULL aparecen como celdas vacías
- Este backup es compatible con Excel, Google Sheets y LibreOffice

Instrucciones de Importación:
-----------------------------
Para importar estos datos a otro sistema:
1. Abra cada archivo Excel
2. Revise la hoja "Metadata" para conocer la estructura
3. Use la hoja "Datos" para la importación
4. Asegúrese de mantener los tipos de datos correctos

Sistema: VIABILIZA - Sistema de Gestión Ambiental, SST y Social
Versión: 1.0
Proyecto Supabase ID: aneybxvzkmrcmvlhkyhv
${errores.length > 0 ? `\nErrores durante la exportación:\n${errores.join('\n')}` : '\nExportación completada sin errores ✓'}
`;

      zip.file('README.txt', readme);

      // Si hubo errores, agregar archivo de errores
      if (errores.length > 0) {
        const errorLog = `Errores de Exportación - ${new Date().toLocaleString('es-ES')}\n\n${errores.join('\n\n')}`;
        zip.file('export_errors.txt', errorLog);
      }

      // Generar y descargar el ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      link.download = `viabiliza_backup_${timestamp}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Calcular tamaño aproximado
      const tamanoMB = (zipBlob.size / (1024 * 1024)).toFixed(2);

      toast({
        title: "✅ Exportación completada",
        description: `${tablasExportadas} tablas exportadas (${tamanoMB} MB)${errores.length > 0 ? ` - ${errores.length} errores` : ''}`,
      });
    } catch (error) {
      console.error('Error en exportación completa:', error);
      toast({
        title: "Error",
        description: "Error al generar el archivo ZIP de exportación",
        variant: "destructive",
      });
    } finally {
      setExportando(false);
      setProgresoExportacion(0);
      setTablaActual("");
    }
  };

  return (
    <div className="space-y-6">
      {/* Estadísticas Generales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tablas</CardTitle>
            <Table className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{estadisticas?.totalTablas || 0}</div>
            <p className="text-xs text-muted-foreground">Tablas principales</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Registros</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {estadisticas?.totalRegistros.toLocaleString('es-ES') || 0}
            </div>
            <p className="text-xs text-muted-foreground">En todas las tablas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Estado</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-sm font-medium">Activa</span>
            </div>
            <p className="text-xs text-muted-foreground">Funcionando correctamente</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Última Actualización</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xs font-medium">
              {estadisticas?.ultimaActualizacion || 'N/A'}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={cargarInformacionDB}
              disabled={loading}
              className="h-6 px-2 mt-1"
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Información de Conexión */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Información de Conexión
          </CardTitle>
          <CardDescription>
            Detalles de la conexión a Supabase
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">URL del Proyecto</Label>
              <p className="text-sm font-mono bg-muted p-2 rounded mt-1">
                https://aneybxvzkmrcmvlhkyhv.supabase.co
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Proyecto ID</Label>
              <p className="text-sm font-mono bg-muted p-2 rounded mt-1">
                aneybxvzkmrcmvlhkyhv
              </p>
            </div>
          </div>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Para gestión avanzada de la base de datos, accede al{" "}
              <a 
                href="https://supabase.com/dashboard/project/aneybxvzkmrcmvlhkyhv" 
                target="_blank" 
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                Dashboard de Supabase
              </a>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Tablas de la Base de Datos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Table className="w-5 h-5" />
            Tablas de la Base de Datos
          </CardTitle>
          <CardDescription>
            Información sobre las tablas principales del sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              tablas.map((tabla) => (
                <div 
                  key={tabla.nombre}
                  className="flex items-center justify-between p-4 border rounded-lg bg-card hover:bg-accent/5 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Table className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <h4 className="font-medium">{tabla.nombre}</h4>
                        <p className="text-sm text-muted-foreground">
                          {tabla.registros.toLocaleString('es-ES')} registros
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {tabla.registros > 0 ? 'Con datos' : 'Vacía'}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Exportación Completa */}
      {exportando && (
        <Card className="border-primary">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-primary animate-pulse" />
                  <div>
                    <h4 className="font-semibold">Exportando Base de Datos</h4>
                    <p className="text-sm text-muted-foreground">
                      Procesando: {tablaActual}
                    </p>
                  </div>
                </div>
                <span className="text-sm font-medium">{Math.round(progresoExportacion)}%</span>
              </div>
              <Progress value={progresoExportacion} />
              <p className="text-xs text-muted-foreground">
                Este proceso puede tardar algunos minutos dependiendo del tamaño de la base de datos
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Acciones Rápidas */}
      <Card>
        <CardHeader>
          <CardTitle>Acciones Rápidas</CardTitle>
          <CardDescription>
            Herramientas de mantenimiento y gestión
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Separator />

            {/* Otras acciones */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button 
                variant="outline" 
                className="h-auto flex-col items-start p-4"
                onClick={cargarInformacionDB}
                disabled={loading || exportando}
              >
                <RefreshCw className={`h-5 w-5 mb-2 ${loading ? 'animate-spin' : ''}`} />
                <span className="font-semibold">Actualizar Estadísticas</span>
                <span className="text-xs text-muted-foreground">
                  Recargar información de la base de datos
                </span>
              </Button>

              <Button 
                variant="outline" 
                className="h-auto flex-col items-start p-4"
                onClick={() => window.open('https://supabase.com/dashboard/project/aneybxvzkmrcmvlhkyhv/editor', '_blank')}
                disabled={exportando}
              >
                <Database className="h-5 w-5 mb-2" />
                <span className="font-semibold">Editor SQL</span>
                <span className="text-xs text-muted-foreground">
                  Abrir editor de consultas SQL
                </span>
              </Button>

              <Button 
                variant="outline" 
                className="h-auto flex-col items-start p-4"
                onClick={() => window.open('https://supabase.com/dashboard/project/aneybxvzkmrcmvlhkyhv/database/migrations', '_blank')}
                disabled={exportando}
              >
                <HardDrive className="h-5 w-5 mb-2" />
                <span className="font-semibold">Migraciones</span>
                <span className="text-xs text-muted-foreground">
                  Ver historial de migraciones
                </span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Label = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <label className={className}>{children}</label>
);
